import {useEffect, useState} from 'react';
import ChatBubble from '../ChatBubble';
import styles from './Chat.module.scss';

export interface ChatProps {
  messageList: string[];
  stacked?: boolean;
  variant?:
    | 'avatar'
    | 'character-left'
    | 'character-right'
    | 'character-bottom';
  imageUrl?: string;
  autoForward?: {
    enabled?: boolean;
    msMessageDelay?: number;
  };
  onChatEnd?(): void;
}

function Chat({
  messageList = [],
  imageUrl,
  stacked,
  variant = 'character-left',
  autoForward = {enabled: false, msMessageDelay: 100},
  onChatEnd,
}: ChatProps): JSX.Element {
  const [renderedMessageList, setRenderedMessageList] = useState(messageList);
  const [messageListIterator, setMessageListIterator] = useState(0);
  const [isChatEnd, setIsChatEnd] = useState(false);

  const isLastMessage = messageListIterator + 1 === messageList.length;

  useEffect(() => {
    if (messageList[0] !== renderedMessageList[0]) {
      // necessary empty wait time when the received messageList changes
      // this allows older browsers to reprocess the animations
      setRenderedMessageList([]);

      setTimeout(() => {
        setRenderedMessageList(messageList);
      }, 300);
    } else {
      setRenderedMessageList(messageList);
    }

    setMessageListIterator(0);
  }, [messageList]);

  if (isChatEnd && isLastMessage) {
    onChatEnd?.();
  }

  return (
    <article className={styles.wrapper}>
      {stacked &&
        renderedMessageList.map((message, index) => {
          if (index < messageListIterator) {
            return (
              <ChatBubble
                key={message}
                message={message}
                imageUrl={imageUrl}
                variant="text-only"
                animationBlocked
              />
            );
          }

          return null;
        })}
      <ChatBubble
        message={renderedMessageList[messageListIterator]}
        imageUrl={imageUrl}
        variant={variant}
        onClick={() => {
          if (messageListIterator < renderedMessageList.length - 1) {
            setMessageListIterator((prev) => prev + 1);
          }
          setIsChatEnd(false);
        }}
        onAnimationEnd={() => {
          if (
            autoForward.enabled &&
            messageListIterator < renderedMessageList.length - 1
          ) {
            setTimeout(
              () => setMessageListIterator((prev) => prev + 1),
              autoForward.msMessageDelay,
            );
          }
          setIsChatEnd(true);
        }}
        clickBlocked={isLastMessage}
        onChatEnd={onChatEnd}
      />
    </article>
  );
}

export default Chat;
